import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useSearch } from "wouter";
import { useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { ArrowLeft, AlertCircle, Loader2 } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { useSession } from "@/hooks/useSession";
import { publicApi } from "@/lib/api";
import { trackPageView, trackEvent } from "@/lib/analytics";
import { SEO, SEOPresets } from "@/components/shared/SEO";
import { STRIPE_PUBLISHABLE_KEY, isStripeConfigured } from "@/config/stripe";
import { Button } from "@/components/ui/button";
import { HoldTimer } from "@/components/shared/HoldTimer";
import { BookingSummary } from "@/components/checkout/BookingSummary";
import { PaymentForm } from "@/components/checkout/PaymentForm";
import { DEFAULT_TIMEZONE } from "@shared/timezone-utils";

// Initialize Stripe outside component to prevent re-initialization
const stripePromise = isStripeConfigured()
  ? loadStripe(STRIPE_PUBLISHABLE_KEY)
  : null;

interface PassengerData {
  fullName: string;
  phone: string;
  email: string;
  dateOfBirth: string;
}

interface PaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
}

interface BatchBookingApiResponse {
  success: boolean;
  replayed?: boolean;
  bookings: Array<{
    id: number;
    ticketNumber: string;
    qrCode: string;
    status: string;
    paymentStatus: string;
    passengerName: string;
    seatNumber: string;
    price: string;
  }>;
}

export function PaymentPage() {
  const { t } = useTranslation();
  const { slug, tenant } = useTenant();
  const { sessionId, clearSession } = useSession();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);

  // State for payment flow
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(true);
  const [intentError, setIntentError] = useState<string | null>(null);
  const [isCreatingBookings, setIsCreatingBookings] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // Parse query params
  const tripId = parseInt(searchParams.get("tripId") || "0", 10);
  const seats = searchParams.get("seats")?.split(",").filter(Boolean) || [];
  const tripType = (searchParams.get("tripType") || "one-way") as "one-way" | "round-trip";
  const date = searchParams.get("date") || "";
  const fromId = searchParams.get("from") || "";
  const toId = searchParams.get("to") || "";
  const price = parseFloat(searchParams.get("price") || "0");
  const routeSegmentId = parseInt(searchParams.get("segmentId") || "0", 10);
  const originCityName = searchParams.get("originCity") || "";
  const destinationCityName = searchParams.get("destinationCity") || "";
  const departureTime = searchParams.get("departureTime") || "";
  // Each end's own zone, carried from checkout (PR B3).
  const departureTimezone = searchParams.get("departureTimezone") || "";
  const arrivalTimezone = searchParams.get("arrivalTimezone") || "";
  const arrivalTime = searchParams.get("arrivalTime") || "";
  const holdExpiresAt = searchParams.get("holdExpiresAt") || "";
  const discountAmount = parseFloat(searchParams.get("discountAmount") || "0");
  const discountCode = searchParams.get("discountCode") || "";
  // Tenant discount ids are UUID strings — the old parseInt() here mangled
  // them (NaN / leading digits), silently dropping the discount server-side.
  const discountId = searchParams.get("discountId") || null;

  // Parse passenger data from URL
  const passengers: PassengerData[] = useMemo(() => {
    const passengersParam = searchParams.get("passengers");
    if (!passengersParam) return [];
    try {
      return JSON.parse(decodeURIComponent(passengersParam));
    } catch {
      return [];
    }
  }, [searchString]);

  // Get tenant timezone
  const timezone = tenant?.timezone || DEFAULT_TIMEZONE;

  // Calculate totals
  const subtotal = price * seats.length;
  const total = Math.max(0, subtotal - discountAmount);
  const totalInCents = Math.round(total * 100);

  // Get primary contact email for Stripe receipt
  const primaryEmail = passengers[0]?.email || undefined;

  // Track if payment attempted event has been fired
  const paymentAttemptTracked = useRef(false);

  // Track page view
  useEffect(() => {
    if (slug) {
      trackPageView("payment", slug);
    }
  }, [slug]);

  // Create PaymentIntent on mount
  useEffect(() => {
    if (!slug || !sessionId || totalInCents <= 0 || seats.length === 0) {
      setIsCreatingIntent(false);
      setIntentError("Invalid booking data");
      return;
    }

    if (!isStripeConfigured()) {
      setIsCreatingIntent(false);
      setIntentError("Payment system is not configured");
      return;
    }

    const createIntent = async () => {
      try {
        setIsCreatingIntent(true);
        setIntentError(null);

        const response = await publicApi.post<PaymentIntentResponse>(
          `/api/public/${slug}/payments/create-intent`,
          {
            amount: totalInCents,
            currency: "usd",
            sessionId,
            passengerEmail: primaryEmail,
          }
        );

        setClientSecret(response.clientSecret);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to initialize payment";
        setIntentError(message);
      } finally {
        setIsCreatingIntent(false);
      }
    };

    createIntent();
  }, [slug, sessionId, totalInCents, primaryEmail]);

  // Handle successful payment - create bookings
  const handlePaymentSuccess = async (confirmedPaymentIntentId: string) => {
    if (!slug || !sessionId || passengers.length !== seats.length) {
      setBookingError("Invalid booking data");
      return;
    }

    setIsCreatingBookings(true);
    setBookingError(null);

    try {
      // ONE atomic batch POST for the whole party (PR1 — checkout
      // atomicity). The server recomputes every price from authoritative
      // config, creates all bookings in a single transaction keyed on the
      // PaymentIntent, and returns the existing bookings on an exact retry —
      // so this call is safe to repeat after a network drop.
      const response = await publicApi.post<BatchBookingApiResponse>(
        `/api/public/${slug}/bookings/batch`,
        {
          tripId,
          routeSegmentId,
          sessionId,
          paymentIntentId: confirmedPaymentIntentId,
          termsAccepted: true,
          tripType,
          discountId: discountId || undefined,
          passengers: passengers.map((passenger, index) => ({
            passengerName: passenger.fullName,
            passengerPhone: passenger.phone || null,
            passengerEmail: passenger.email || null,
            dateOfBirth: passenger.dateOfBirth || null,
            seatNumber: seats[index],
          })),
        }
      );

      // Track booking completed event
      trackEvent("booking_completed", {
        tripId,
        seatCount: seats.length,
        totalPrice: total,
        discountAmount,
      });

      // Navigate to confirmation page with first booking's ticket number
      const firstBooking = response.bookings?.[0];
      if (firstBooking?.ticketNumber) {
        setLocation(`/confirmation/${firstBooking.ticketNumber}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create booking";
      setBookingError(message);
      setIsCreatingBookings(false);
    }
  };

  // Handle payment error
  const handlePaymentError = (error: string) => {
    console.error("Payment error:", error);
    // Error is shown in PaymentForm component
  };

  // Track payment attempted when user clicks pay
  const handlePaymentAttempted = () => {
    if (!paymentAttemptTracked.current) {
      trackEvent("payment_attempted", {
        tripId,
        seatCount: seats.length,
        totalPrice: total,
        discountApplied: discountAmount > 0,
      });
      paymentAttemptTracked.current = true;
    }
  };

  // Handle hold expiration
  const handleHoldExpire = () => {
    clearSession();
    setLocation("/");
  };

  // Handle back to checkout
  const handleBackToCheckout = () => {
    const params = new URLSearchParams({
      tripId: tripId.toString(),
      seats: seats.join(","),
      passengers: searchParams.get("passengers") || "1",
      tripType,
      date,
      from: fromId,
      to: toId,
      price: price.toString(),
    });
    setLocation(`/checkout?${params.toString()}`);
  };

  // Validation: Check if we have required data
  if (!tripId || seats.length === 0 || passengers.length === 0) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-center">
              {tenant?.publicLogoUrl ? (
                <img
                  src={tenant.publicLogoUrl}
                  alt={tenant.name}
                  className="h-10 object-contain"
                />
              ) : (
                <h1 className="text-2xl font-bold text-primary">{tenant?.name}</h1>
              )}
            </div>
          </div>
        </header>
        <main className="container mx-auto px-4 py-12 flex-1">
          <div className="max-w-md mx-auto text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t("errors.invalidBooking")}</h2>
            <p className="text-muted-foreground mb-6">
              {t("errors.missingInfo")}
            </p>
            <Button onClick={() => setLocation("/")}>
              {t("trips.backToSearch")}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SEO {...SEOPresets.payment} />
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-center">
            {tenant?.publicLogoUrl ? (
              <img
                src={tenant.publicLogoUrl}
                alt={tenant.name}
                className="h-10 object-contain"
              />
            ) : (
              <h1 className="text-2xl font-bold text-primary">{tenant?.name}</h1>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main id="main-content" className="container mx-auto px-4 py-6 flex-1">
        <div className="max-w-2xl mx-auto">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={handleBackToCheckout}
            className="mb-4 -ml-2"
            disabled={isCreatingBookings}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("payment.backToCheckout")}
          </Button>

          {/* Hold Timer */}
          {holdExpiresAt && (
            <div className="mb-6">
              <HoldTimer
                expiresAt={holdExpiresAt}
                onExpire={handleHoldExpire}
              />
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            {/* Payment Form Column */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">{t("payment.title")}</h2>
              <p className="text-sm text-muted-foreground">
                {t("payment.description")}
              </p>

              <div className="bg-card border rounded-lg p-4">
                {isCreatingIntent && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-2 text-muted-foreground">{t("payment.preparingPayment")}</span>
                  </div>
                )}

                {intentError && (
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                    <p className="text-destructive font-medium mb-2">{t("payment.setupFailed")}</p>
                    <p className="text-sm text-muted-foreground mb-4">{intentError}</p>
                    <Button variant="outline" onClick={handleBackToCheckout}>
                      {t("common.back")}
                    </Button>
                  </div>
                )}

                {bookingError && (
                  <div className="p-3 mb-4 text-sm text-destructive bg-destructive/10 rounded-md" role="alert">
                    <strong>{t("payment.bookingError")}:</strong> {bookingError}
                    <p className="mt-1 text-xs">{t("payment.bookingErrorDescription")}</p>
                  </div>
                )}

                {isCreatingBookings && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-2 text-muted-foreground">{t("payment.creatingBooking")}</span>
                  </div>
                )}

                {clientSecret && stripePromise && !isCreatingIntent && !isCreatingBookings && (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      appearance: {
                        theme: "stripe",
                        variables: {
                          colorPrimary: "hsl(var(--primary))",
                        },
                      },
                    }}
                  >
                    <PaymentForm
                      totalAmount={total.toFixed(2)}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPaymentError={handlePaymentError}
                      onPaymentAttempted={handlePaymentAttempted}
                      disabled={isCreatingBookings}
                    />
                  </Elements>
                )}
              </div>
            </div>

            {/* Booking Summary Column */}
            <div className="lg:sticky lg:top-6 h-fit">
              <BookingSummary
                originCity={originCityName}
                destinationCity={destinationCityName}
                date={date}
                departureTime={departureTime}
                arrivalTime={arrivalTime}
                timezone={timezone}
                departureTimezone={departureTimezone || undefined}
                arrivalTimezone={arrivalTimezone || undefined}
                seats={seats}
                pricePerPassenger={price}
                tripType={tripType}
                discountAmount={discountAmount}
                discountCode={discountCode || undefined}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-card">
        <div className="container mx-auto px-4 py-4">
          <p className="text-center text-sm text-muted-foreground">
            {t("common.poweredBy")}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default PaymentPage;
